'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import React from 'react';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { HtmlDescription } from '../shared/html-description';
import { useMediaQuery } from '@eliadmin/core/src/hooks/use-media-query';

export type BlogCardProps = {
  className?: {
    main?: string;
    link?: string;
    image?: string;
    title?: string;
    date?: string;
    contentContainer?: string;
    content?: string;
  };
  blog: Blog;
  firstBlog?: boolean;
  titleAs?: 'h2' | 'h3';
};

export function BlogCard({ blog, firstBlog, titleAs: TitleTag = 'h3' }: BlogCardProps) {
  const textContent = getTextFromHTML(blog.content);
  const locale = useLocaleStore(state => state.currentLocale);

  const isMobile = useMediaQuery('(max-width: 640px)');

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  if (firstBlog && !isMobile)
    return (
      <Link
        prefetch={false}
        href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
        className="relative col-span-1 flex items-center justify-between gap-6 sm:col-span-2 lg:col-span-3 lg:gap-12 xl:gap-16"
      >
        <Image
          src={'/images/sign.svg'}
          alt="Danceana"
          width={0}
          height={0}
          className="absolute -left-20 top-4 z-[5] h-4 w-auto sm:top-5 lg:top-6"
        />
        <Image
          src={blog.image?.url || '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          width={0}
          height={0}
          className="h-[440px] w-full min-w-[50px] max-w-[715px] object-cover object-center lg:h-[500px] xl:h-[550px]"
          sizes="50vw"
        />
        <div className="flex w-full min-w-[260px] max-w-[460px] flex-col items-start justify-start gap-6">
          <div className="flex w-full flex-col items-start justify-start gap-2">
            <TitleTag data-ea-visual-tag="h3" className="line-clamp-3 font-heading text-3xl font-medium lg:text-4xl xl:text-5xl">
              {blog.name}
            </TitleTag>
            <HtmlDescription
              content={textContent}
              className="line-clamp-2 w-full text-black/70"
              size="base"
            />
          </div>
          <p className="text-sm font-normal text-black/70 lg:text-base">{formattedDate}</p>
        </div>
      </Link>
    );
  return (
    <Link
      prefetch={false}
      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
      className="relative col-span-1 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6"
    >
      <Image
        src={'/images/sign.svg'}
        alt="Danceana"
        width={0}
        height={0}
        className="absolute -left-16 top-4 z-[5] h-3.5 w-auto sm:top-5 lg:top-6"
      />
      <Image
        src={blog.image?.url || '/images/placeholder.png'}
        alt={resolveImageAlt(blog?.image, blog?.name)}
        width={0}
        height={0}
        className="h-[230px] w-full object-cover object-center sm:h-[250px] md:h-[270px] lg:h-[292px] xl:h-[300px]"
        sizes="400px"
      />
      <div className="flex w-full flex-col items-start justify-start gap-3">
        <TitleTag data-ea-visual-tag="h3" className="line-clamp-2 h-16 font-heading text-xl font-medium text-black lg:h-16 lg:text-2xl">
          {blog.name}
        </TitleTag>
        <HtmlDescription
          content={textContent}
          className="line-clamp-2 w-full text-black/70"
          size="base"
        />
      </div>
      <p className="text-sm font-normal text-black/70 lg:text-base">{formattedDate}</p>
    </Link>
  );
}
